import type { AdminInspectorAvailability, AdminInspectorStatus } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Status + availability badge chips for the inspectors list + detail
 * drawer (AUT-125). Colours mirror the mockup's `badge--{success,warning,
 * danger,info,neutral}` classes via MUI Chip `color`. Labels resolve
 * through i18n (CLAUDE.md rule #11).
 *
 * KYC status here is the SESSION status (`AdminInspectorKycSummaryDto`'s
 * `sessionStatus`) — a string-from-the-wire (the api doesn't lock the
 * enum since the provider lifecycle is provider-driven). A null /
 * unrecognised value renders the neutral "—" chip.
 */

type ChipColor = ChipProps['color'];

const STATUS_COLOR: Record<AdminInspectorStatus, ChipColor> = {
  unverified: 'default',
  profile_filled: 'default',
  idcheck_pending: 'info',
  idcheck_passed: 'success',
  idcheck_failed: 'error',
  bank_pending: 'warning',
  active: 'success',
  reverification_required: 'info',
  suspended: 'warning',
  banned: 'error',
};

const AVAILABILITY_COLOR: Record<AdminInspectorAvailability, ChipColor> = {
  online: 'success',
  offline: 'default',
  busy: 'warning',
};

/** Recognised KYC session statuses → chip colour. Anything else → neutral. */
const KYC_SESSION_COLOR: Record<string, ChipColor> = {
  pending: 'warning',
  in_progress: 'info',
  passed: 'success',
  failed: 'error',
  manual_review: 'warning',
  expired: 'default',
  reset: 'default',
};

export const StatusBadge = ({ status }: { status: AdminInspectorStatus }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip size="small" color={STATUS_COLOR[status]} label={t(`inspectors.status.${status}`)} />
  );
};

export const AvailabilityBadge = ({
  availability,
}: {
  availability: AdminInspectorAvailability;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      variant="outlined"
      color={AVAILABILITY_COLOR[availability]}
      label={t(`inspectors.availability.${availability}`)}
    />
  );
};

/**
 * KYC session-state chip. `sessionStatus` is the wire string from
 * `AdminInspectorKycSummaryDto`; null (no session yet) / unrecognised
 * values render the neutral "—" placeholder.
 */
export const KycSessionBadge = ({
  sessionStatus,
}: {
  sessionStatus: string | null;
}): ReactElement => {
  const { t } = useTranslation();
  if (sessionStatus === null) {
    return <Chip size="small" color="default" label={t('inspectors.kyc.none')} />;
  }
  const color = KYC_SESSION_COLOR[sessionStatus] ?? 'default';
  return <Chip size="small" color={color} label={t(`inspectors.kyc.${sessionStatus}`)} />;
};

/**
 * KYC chip derived from the inspector's FSM `status` for the list grid.
 * The list DTO (`AdminInspectorListItemDto`) does NOT carry KYC session
 * status — only the detail does — so the list cell derives a coarse KYC
 * label from the FSM. Mapping:
 *   - `idcheck_passed` / `bank_pending` / `active` / `suspended` → ID пройден (success)
 *   - `idcheck_pending` → на проверке (info)
 *   - `idcheck_failed` / `banned` → не пройден (error)
 *   - `reverification_required` → нужна переверификация (warning)
 *   - `unverified` / `profile_filled` → не начат / анкета заполнена (neutral)
 */
const KYC_FROM_STATUS_COLOR: Record<AdminInspectorStatus, ChipColor> = {
  unverified: 'default',
  profile_filled: 'default',
  idcheck_pending: 'info',
  idcheck_passed: 'success',
  idcheck_failed: 'error',
  bank_pending: 'success',
  active: 'success',
  reverification_required: 'warning',
  suspended: 'success',
  banned: 'error',
};

export const InspectorKycStatusBadge = ({
  status,
}: {
  status: AdminInspectorStatus;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color={KYC_FROM_STATUS_COLOR[status]}
      label={t(`inspectors.kyc.byStatus.${status}`)}
    />
  );
};
